import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import '~/components/routes/campaign/components/product-group/product-group.scss';
import ErrorLabel from '~/components/shared/error-label/ErrorLabel';
import type { ProductBundle, ProductWithBundleId } from '~/models/CampaignModel';
import { FlowMode } from '~/models/FlowMode';
import { ProductType } from '~/models/product/ProductType';
import type { ProductTypeConfig } from '~/models/product/ProductTypeConfig';
import { BYOD_PRODUCT_ID } from '~/store/campaign/byod.constants';
import { getFlowMode } from '../campaign.utils';
import { useBasket } from '../useBasket';
import { ByodBundle } from './ByodBundle';
import ProductGroupHeader from './product-group/components/product-group-header/ProductGroupHeader';
import { ProductBundleNextRowButton } from './ProductBundleNextRowButton';

import ProductLimit from '~/components/shared/product-limit/ProductLimit';
import { FlexipakkenChoice } from './product-group/components/campaign-choice/FlexiPakkenChoice';
import { ProductBundleComponent } from './ProductBundle';

export const ProductGroupComponent: React.FC<{ group: ProductBundle<ProductWithBundleId>[]; config: ProductTypeConfig; type: ProductType }> = ({
  group,
  config,
  type,
}) => {
  const selectedProducts = useBasket((s) => s.selectedProducts);
  const stepError = useBasket((s) => s.stepError);
  const setStepError = useBasket((s) => s.setStepError);
  const { campaignUrl } = useParams();
  const isFleksiPakken = campaignUrl === 'fleksipakken';
  const isFleksiPakkenPremiumStep = isFleksiPakken && type === ProductType.Premium;

  useInitBundle(group, type, selectedProducts);

  // reset error for receivers on product change
  if (stepError && selectedProducts.some((p) => p.productType === ProductType.Receiver)) {
    setStepError(undefined);
  }
  const shouldShowProductLimit = useMemo(() => {
    const bundleIds = group.map((b) => b.id);
    return bundleIds.length === selectedProducts.filter((p) => bundleIds.includes(p.bundleId)).length;
  }, [selectedProducts, group]);

  return (
    <div className="product-group" data-type={type}>
      <ProductGroupHeader config={config} type={type} selectedProducts={selectedProducts} />
      {/* Add separate selection option for receivers where users can select NOT to buy/rent a box */}
      {type === ProductType.Receiver && (
        <>
          <ByodBundle group={group} />
          <ErrorLabel hasError={!!stepError} focusOnError={true}>
            {stepError}
          </ErrorLabel>
        </>
      )}
        

      {isFleksiPakkenPremiumStep && (
        <>
          <FlexipakkenChoice />
          <h3 style={{ marginBottom: '1.5rem' }}>Legg til ekstra innhold</h3>
        </>
      )}

      {/* PRODUCT BUNDLES */}
      {group.map((groupItem, index) => (
        <ProductBundleComponent
          key={groupItem.id}
          group={groupItem}
          config={config}
          type={type}
          index={index}
          previousBundleId={group[index - 1]?.id}
        />
      ))}
      <ProductBundleNextRowButton label={config?.buttonLabel || 'Jeg trenger et produkt til'} group={group} />
      {shouldShowProductLimit && <ProductLimit type={type} />}
    </div>
  );
};

const useInitBundle = (group: ProductBundle<ProductWithBundleId>[], type: ProductType, selectedProducts: ProductWithBundleId[]) => {
  const showBundle = useBasket((s) => s.showBundle);
  const openBundle = useBasket((s) => s.openBundle);
  const setActiveBundle = useBasket((s) => s.setActiveBundle);

  // biome-ignore lint/correctness/useExhaustiveDependencies: We dont want this to run every time selected products change
  useEffect(() => {
    if (group.length === 0) return;

    /* Sets up initial configurations based on flowMode */
    const flowMode = getFlowMode(type);
    if (flowMode === FlowMode.Progressive) {
      // Close and hide all bundles on initialization
      group.forEach((bundle) => {
        openBundle(bundle.id, false);
        showBundle(bundle.id, false);
      });

      // Leave all receiver bundles closed when BYOD is selected
      if (type === ProductType.Receiver && selectedProducts.some((p) => p.id === BYOD_PRODUCT_ID)) {
        return;
      }

      const selectedBundles = group.filter((bundle) => bundle.offers.some((o) => selectedProducts.some((p) => p.id === o.id)));
      // If none are selected before
      if (selectedBundles.length === 0) {
        // if (selectors.hasSelectedBYOD && type === ProductType.Receiver) return;
        openBundle(group[0].id, true);
        showBundle(group[0].id, true);
        setActiveBundle(type, group[0].id);
        return;
      }

      // Show all bundles with selected products
      for (const bundle of selectedBundles) {
        showBundle(bundle.id, true)
      }
      // Set the last bundle of those to the active one
      setActiveBundle(type, selectedBundles[selectedBundles.length - 1].id);
    }

    if (flowMode === FlowMode.Open) {
      group.forEach((bundle) => {
        showBundle(bundle.id, true);
        openBundle(bundle.id, true);
      });
      setActiveBundle(type, group[group.length - 1].id);
    }
  }, [group, type, showBundle, openBundle, setActiveBundle]);
};
